import { v4 as uuidV4 } from 'uuid';
import { groupBy } from 'lodash-es';
import { parseAbsolute, parseZonedDateTime } from '@internationalized/date';
import { convertRouteLegToSuggestedRouteLegCandidate, getUpdatedRouteLegValues } from '@lp/features/RoutePlanner/utils';
import { isPrimaryHospital } from '@lp/features/RoutePlanner/utils/case-locations';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { LegFormValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  AccommodationLeg,
  AddLegTypes,
  AirportRouteLocation,
  AviaLegTypes,
  CommercialFlightAviaLeg,
  CurriServiceGroundLeg,
  GroundLegTypes,
  HubTypeEnum,
  LegLocationTypeEnum,
  LogisticsProjectionType,
  RouteLegType,
  RouteLegTypeEnum,
  RouteLocationType,
  RouteTypeEnum,
  SegmentTypeEnum,
  SelfServicedGroundLeg,
  SuggestedRouteType,
  SupplementalLiftAviaLeg,
  TmdxAffiliateGroundLeg,
  TmdxDriveAffiliateGroundLeg,
  TmdxDriveTedsGroundLeg,
  TmdxJetAviaLeg,
  TxCenterProviderAviaLeg,
  TxCenterProviderGroundLeg,
  UpdateLegParams,
} from '@/api/apis/route';
import { CaseResourceType, CaseStatus } from '@/api/apis/case';
import { RouteTypeSettingsEnum } from '@/api/apis/mission-planner';
import { ApiResponseBody } from '@/api/types';
import { convertMsToTime, formatDateTimeStringToTimeZone, getTzDateTimeString } from '@/utils/helpers';
import { RouteFilterType, RouteSegmentType } from '@/types/route';
import {
  AIR_BLOCK_TIME,
  AVIA_LEG_TYPES,
  getSegmentMapKey,
  isAviaLegType,
  isGroundLegType,
  REPOSITION_SEGMENT_TYPES,
  sortSegmentsByTime,
} from '@/utils/route';

export const getMainRouteLegs = (
  segments: RouteSegmentType[],
  legs: (AviaLegTypes | GroundLegTypes)[],
): Map<string, (AviaLegTypes | GroundLegTypes)[]> => {
  return segments.reduce<Map<string, (AviaLegTypes | GroundLegTypes)[]>>((acc, segment) => {
    const segmentLegs = legs.filter((leg) => leg.segmentType === segment.type);

    acc.set(getSegmentMapKey(segment), segmentLegs);

    return acc;
  }, new Map());
};

export const getCustomRouteData = (
  segments: RouteSegmentType[],
  legs: (AviaLegTypes | GroundLegTypes)[],
): { segments: RouteSegmentType[]; legs: Map<string, (AviaLegTypes | GroundLegTypes)[]> } => {
  const mainSegments = [...segments];
  const legsMap = new Map() as Map<string, (AviaLegTypes | GroundLegTypes)[]>;

  if (!legs.length) {
    return {
      segments: mainSegments,
      legs: legsMap,
    };
  }

  const combinedLegs = new Map<string, (AviaLegTypes | GroundLegTypes)[]>();
  const groupKeyByLegId = new Map<string, string>();

  legs.forEach((leg) => {
    const isNewChain = !combinedLegs.size || !leg.chainPosition.previousLegId;

    if (isNewChain) {
      combinedLegs.set(leg.id, [leg]);
      groupKeyByLegId.set(leg.id, leg.id);
      return;
    }

    const groupKey = groupKeyByLegId.get(leg.chainPosition.previousLegId!);

    if (groupKey) {
      combinedLegs.get(groupKey)!.push(leg);
      groupKeyByLegId.set(leg.id, groupKey);
    }
  });

  combinedLegs.forEach((currentLegs) => {
    const leg = currentLegs[0];
    const lastLeg = currentLegs[currentLegs.length - 1];

    const departureTime = parseAbsolute(leg.departureTime, leg.departureLocalTimezone).toString();
    const arrivalTime = parseAbsolute(leg.arrivalTime, leg.arrivalLocalTimezone).toString();

    const newSegment = {
      id: leg.chainPosition.groupId || uuidV4(),
      startLocation: leg.departureLocation,
      type: leg.segmentType,
      startTime: departureTime,
      actualStartTime: departureTime,
      minDate: departureTime,
      endLocation: lastLeg.arrivalLocation,
      endTime: arrivalTime,
      actualEndTime: arrivalTime,
    } as RouteSegmentType;

    mainSegments.push(newSegment);

    legsMap.set(getSegmentMapKey(newSegment), currentLegs);
  });

  const sortedSegments = sortSegmentsByTime(mainSegments);

  return { segments: sortedSegments, legs: legsMap };
};

const getFilteredRouteByType = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
  filterType: RouteFilterType,
): { segments: RouteSegmentType[]; legs: Map<string, (AviaLegTypes | GroundLegTypes)[]> } => {
  if (filterType === RouteFilterType.Full) {
    return { segments, legs };
  }

  const filteredLegsMap = new Map<string, (AviaLegTypes | GroundLegTypes)[]>();
  const filteredSegments: RouteSegmentType[] = [];

  segments.forEach((segment) => {
    const segmentLegs = legs.get(getSegmentMapKey(segment)) || [];

    if (!segmentLegs.length) {
      return;
    }

    const firstLeg = segmentLegs[0];
    const lastLeg = segmentLegs[segmentLegs.length - 1];
    const startTime = parseAbsolute(firstLeg.departureTime, firstLeg.departureLocalTimezone).toString();

    const newSegment = {
      ...segment,
      startLocation: firstLeg.departureLocation,
      endLocation: lastLeg.arrivalLocation,
      startTime,
      actualStartTime: startTime,
    } as RouteSegmentType;

    filteredLegsMap.set(getSegmentMapKey(newSegment), segmentLegs);
    filteredSegments.push(newSegment);
  });

  return { segments: filteredSegments, legs: filteredLegsMap };
};

export const CUSTOM_SEGMENTS = [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom];

export const convertRouteList = (
  segments: RouteSegmentType[],
  legs: (AviaLegTypes | GroundLegTypes)[],
  filterType: RouteFilterType = RouteFilterType.Full,
): { segments: RouteSegmentType[]; legs: Map<string, (AviaLegTypes | GroundLegTypes)[]> } => {
  const mainActions = legs.filter((r) => !CUSTOM_SEGMENTS.includes(r.segmentType));
  const customActions = legs.filter((r) => CUSTOM_SEGMENTS.includes(r.segmentType));

  const mainRouteLegs = getMainRouteLegs(segments, mainActions);
  const { segments: newSegments, legs: routeLegs } = getCustomRouteData(segments, customActions);

  const legsMap = new Map([...mainRouteLegs, ...routeLegs]);

  return getFilteredRouteByType(newSegments, legsMap, filterType);
};

export type LogisticsMetricsItem = {
  provider: RouteLegTypeEnum;
  actualTimeMs: number;
  blockTimeMs?: number;
};

const LOGISTICS_METRIC_LEG_TYPES: RouteLegTypeEnum[] = [
  RouteLegTypeEnum.TmdxJetAviaLeg,
  RouteLegTypeEnum.SupplementalLiftAviaLeg,
  RouteLegTypeEnum.TxCenterProviderAviaLeg,
  RouteLegTypeEnum.TmdxAffiliateGroundLeg,
  RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
  RouteLegTypeEnum.TxCenterProviderGroundLeg,
  RouteLegTypeEnum.SelfServicedGroundLeg,
];

export const getLogisticsMetrics = (legs: (AviaLegTypes | GroundLegTypes)[]): LogisticsMetricsItem[] => {
  const legsByType = groupBy(legs, (leg) => leg.legType) as Partial<
    Record<RouteLegTypeEnum, (AviaLegTypes | GroundLegTypes)[]>
  >;

  return LOGISTICS_METRIC_LEG_TYPES.reduce<LogisticsMetricsItem[]>((acc, type) => {
    const legsList = legsByType[type];

    if (!legsList?.length) {
      return acc;
    }

    const isAviaProvider = AVIA_LEG_TYPES.includes(type);

    const legsTime = legsList.reduce((total, leg) => {
      const departureTime = new Date(leg.departureTime).getTime();
      const arrivalTime = new Date(leg.arrivalTime).getTime();

      return total + (arrivalTime - departureTime);
    }, 0);

    return [
      ...acc,
      {
        provider: type,
        blockTimeMs: isAviaProvider ? legsTime : undefined,
        actualTimeMs: isAviaProvider ? legsTime - legsList.length * AIR_BLOCK_TIME : legsTime,
      },
    ];
  }, []);
};

export const getDryRunLogisticsMetrics = (
  mainLegs: (AviaLegTypes | GroundLegTypes)[],
  dryRunLegs: (AviaLegTypes | GroundLegTypes)[],
  donorHospitalId?: string,
): LogisticsMetricsItem[] => {
  if (!dryRunLegs.length) return [];

  const toDonorLastLegIndex = mainLegs.findLastIndex(
    (l) => l.segmentType === SegmentTypeEnum.hospital && isPrimaryHospital(l.arrivalLocation, { donorHospitalId }),
  );
  const toDonorOrLegs = mainLegs
    .slice(0, toDonorLastLegIndex + 1)
    .filter((l) => [SegmentTypeEnum.reposition, SegmentTypeEnum.hospital].includes(l.segmentType))
    .reduce(
      (acc, l, _, array) => {
        if (isGroundLegType(l) || !REPOSITION_SEGMENT_TYPES.includes(l.segmentType)) {
          return [...acc, l];
        }

        if (l.arrivalLocation.locationType === LegLocationTypeEnum.airport) {
          const endLocationId = l.arrivalLocation.locationId;
          const hasConnection = array.some(
            (leg) =>
              isAviaLegType(leg) &&
              leg.arrivalLocation.locationType === LegLocationTypeEnum.airport &&
              leg.arrivalLocation.locationId === endLocationId,
          );

          if (hasConnection) {
            return [...acc, l];
          }
        }

        return acc;
      },
      [] as (AviaLegTypes | GroundLegTypes)[],
    );

  return getLogisticsMetrics(toDonorOrLegs.concat(dryRunLegs));
};

export const convertDurationToHours = (durationMs: number) => {
  const { hours, minutes } = convertMsToTime(durationMs);
  const minutesInFractions = Math.round((minutes / 60 + Number.EPSILON) * 10) / 10;

  return hours + minutesInFractions;
};

export const durationInSecondsToHours = (seconds: number) => (seconds / 60 / 60).toFixed(2);

const getLegStartTimeMs = (leg: RouteLegType): number => {
  const startTimeValue = leg.legType === RouteLegTypeEnum.AccommodationLeg ? leg.checkInAt : leg.departureTime;
  const legStartTimeUtc = parseZonedDateTime(getTzDateTimeString(startTimeValue)).toAbsoluteString();

  return new Date(legStartTimeUtc).getTime();
};

export const getFilteredLegsByStartTime = (legs: RouteLegType[], status: CaseStatus): string[] => {
  if (status === CaseStatus.PendingLogistics) {
    return legs.map((l) => l.id);
  }

  const now = Date.now();

  return legs.filter((l) => now < getLegStartTimeMs(l)).map((l) => l.id);
};

export const convertAccommodationLegs = (
  segments: RouteSegmentType[],
  accommodationLegs: AccommodationLeg[],
): { segments: RouteSegmentType[]; accommodationLegs: Map<string, AccommodationLeg[]> } => {
  const mainSegments = [...segments];
  const accommodationLegsMap = new Map() as Map<string, AccommodationLeg[]>;

  accommodationLegs.forEach((leg) => {
    const newSegment = {
      id: uuidV4(),
      startLocation: leg.location,
      type: leg.segmentType,
      startTime: leg.checkInAt,
      actualStartTime: leg.checkInAt,
      minDate: leg.checkOutAt,
      endLocation: leg.location,
      isAccommodation: true,
    } as RouteSegmentType;

    mainSegments.push(newSegment);

    accommodationLegsMap.set(getSegmentMapKey(newSegment), [leg]);
  });

  const sortedSegments = sortSegmentsByTime(mainSegments);

  return { segments: sortedSegments, accommodationLegs: accommodationLegsMap };
};

export const getMultiStopCommercialString = (legs: CommercialFlightAviaLeg[], locations: RouteLocationType[]) => {
  if (legs.length > 1) {
    return legs
      .map((l) => {
        const departureLocation = locations.find(
          (loc) => loc.id === l.departureLocation.locationId,
        ) as AirportRouteLocation;
        const arrivalLocation = locations.find(
          (loc) => loc.id === l.arrivalLocation.locationId,
        ) as AirportRouteLocation;

        return `${departureLocation?.iata} - ${arrivalLocation?.iata}`;
      })
      .join(', ');
  }

  return '';
};

const getUpdatedValues = (data: LegFormValues, leg: RouteLegType, locations: RouteLocationType[] = []) => {
  switch (data.legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TmdxJetAviaLeg,
      });
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as SupplementalLiftAviaLeg,
      });
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TxCenterProviderAviaLeg,
      });
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as CommercialFlightAviaLeg,
      });
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TxCenterProviderGroundLeg,
      });
    case RouteLegTypeEnum.CurriServiceGroundLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as CurriServiceGroundLeg,
      });
    case RouteLegTypeEnum.AccommodationLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as AccommodationLeg,
        locations,
      });
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TmdxAffiliateGroundLeg,
      });
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TmdxDriveTedsGroundLeg,
      });
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as SelfServicedGroundLeg,
      });
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    default:
      return getUpdatedRouteLegValues({
        type: data.legType,
        data,
        leg: leg as TmdxDriveAffiliateGroundLeg,
      });
  }
};

type SaveUpdateLegParams<T extends RouteLegType, R extends AddLegTypes> = {
  logisticsId: string;
  routeId: string;
  version: number;
  leg: T;
  formData: LegFormValues;
  locations?: RouteLocationType[];
  mutation: UseMutateAsyncFunction<ApiResponseBody<void>, unknown, UpdateLegParams<R>, unknown>;
};

export const saveUpdatedLeg = async <T extends RouteLegType, R extends AddLegTypes>({
  logisticsId,
  routeId,
  version,
  leg,
  formData,
  mutation,
  locations = [],
}: SaveUpdateLegParams<T, R>) => {
  const updateObject = getUpdatedValues(formData, leg, locations);

  if (Object.keys(updateObject).length) {
    await mutation({
      logisticsId,
      routeId,
      legId: leg.id,
      version,
      data: updateObject as Partial<R>,
    });
  }
};

const getArrivalHubSegment = (segments: RouteSegmentType[], isMainRoute: boolean, isHubToDhRoute: boolean) => {
  if (isHubToDhRoute) return undefined;

  const arrivalHubSegmentType = isMainRoute ? SegmentTypeEnum.hub : SegmentTypeEnum.dhToHub;

  return segments.find((s) => s.type === arrivalHubSegmentType);
};

type GetSuggestedRouteDataParams = {
  routeType: RouteTypeEnum;
  routeId: string;
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  accommodationLegs: Map<string, AccommodationLeg[]>;
  locations: RouteLocationType[];
  isHubToDhRoute?: boolean;
  groundDistances?: Map<string, number>;
};

const withGroundDistance = (leg: RouteLegType, groundDistances?: Map<string, number>): RouteLegType => {
  const distanceInMeters = groundDistances?.get(leg.id);

  return isGroundLegType(leg) && distanceInMeters !== undefined ? { ...leg, distanceInMeters } : leg;
};

export const getSuggestedRouteData = ({
  routeType,
  routeId,
  segments,
  legs,
  accommodationLegs,
  locations,
  isHubToDhRoute = false,
  groundDistances,
}: GetSuggestedRouteDataParams): SuggestedRouteType => {
  const isMainRoute = routeType === RouteTypeEnum.Main;
  const departureHubSegment = isMainRoute ? segments.find((s) => s.type === SegmentTypeEnum.hospital) : null;
  const arrivalHubSegment = getArrivalHubSegment(segments, isMainRoute, isHubToDhRoute);

  const departureHubId = departureHubSegment?.startLocation.locationId || null;
  const arrivalHubId = arrivalHubSegment ? arrivalHubSegment.endLocation.locationId : null;

  const legList = segments
    .flatMap<RouteLegType>((s) => {
      const segmentLegs = s.isAccommodation ? accommodationLegs : legs;

      return segmentLegs.get(getSegmentMapKey(s)) || [];
    })
    .map((l) => convertRouteLegToSuggestedRouteLegCandidate(withGroundDistance(l, groundDistances), locations));

  return {
    routeId,
    departureHubId,
    arrivalHubId,
    legs: legList,
  };
};

export const getSuggestedRouteCleanUpLeg = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): AviaLegTypes | GroundLegTypes | undefined => {
  const [firstMainLeg] = segments
    .filter((s) => !s.isAccommodation && !CUSTOM_SEGMENTS.includes(s.type))
    .flatMap((s) => legs.get(getSegmentMapKey(s)) || []);

  return firstMainLeg;
};

export const getSuggestedRouteCleanUpTimeMs = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): number | undefined => {
  const cleanUpLeg = getSuggestedRouteCleanUpLeg(segments, legs);

  return cleanUpLeg ? getLegStartTimeMs(cleanUpLeg) : undefined;
};

export const getSuggestedRouteStartTime = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
): string => {
  const cleanUpLeg = getSuggestedRouteCleanUpLeg(segments, legs);

  return formatDateTimeStringToTimeZone(cleanUpLeg?.departureTime, cleanUpLeg?.departureLocalTimezone);
};

export const getLegsToRemove = (legs: RouteLegType[], status: CaseStatus, isReadOnly?: boolean) => {
  if (isReadOnly) return [];

  return getFilteredLegsByStartTime(legs, status);
};

export const hasUnremovableLegs = (legs: RouteLegType[], status: CaseStatus, isReadOnly?: boolean): boolean =>
  getLegsToRemove(legs, status, isReadOnly).length < legs.length;

type AffectedRouteLegsParams = {
  isPartialRoute: boolean;
  mainRouteLegs: RouteLegType[];
  dryRunRouteLegs: RouteLegType[];
  cleanUpTimeMs?: number;
};

const isSurgeonCustomLeg = (leg: RouteLegType): boolean =>
  CUSTOM_SEGMENTS.includes(leg.segmentType) && leg.resources.some((r) => r.resourceType === CaseResourceType.Surgeon);

export const getAffectedRouteLegs = ({
  isPartialRoute,
  mainRouteLegs,
  dryRunRouteLegs,
  cleanUpTimeMs,
}: AffectedRouteLegsParams): RouteLegType[] => {
  if (!isPartialRoute) return [...mainRouteLegs, ...dryRunRouteLegs];

  return mainRouteLegs.filter(
    (l) => !isSurgeonCustomLeg(l) && (cleanUpTimeMs === undefined || getLegStartTimeMs(l) >= cleanUpTimeMs),
  );
};

const PARTIAL_ROUTE_REPLACED_SEGMENTS: Partial<Record<RouteTypeSettingsEnum, SegmentTypeEnum[]>> = {
  [RouteTypeSettingsEnum.Custom_DH_TXC_Hub]: [SegmentTypeEnum.txcenter, SegmentTypeEnum.hub],
  [RouteTypeSettingsEnum.Custom_TXC_Hub]: [SegmentTypeEnum.hub],
};

export const getReplacedSegmentLegs = (
  legs: RouteLegType[],
  routeType: RouteTypeSettingsEnum | null,
): RouteLegType[] => {
  const replacedSegments = routeType ? PARTIAL_ROUTE_REPLACED_SEGMENTS[routeType] : undefined;

  if (!replacedSegments) return [];

  return legs.filter((l) => replacedSegments.includes(l.segmentType));
};

type HubsRemoveList = {
  hubType: HubTypeEnum;
  hubId: string;
};

export const getHubsToRemove = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  removedLegsIds: Set<string>,
  hubs: HubsRemoveList[],
): HubTypeEnum[] => {
  const activeLegs = legs.filter((l) => !removedLegsIds.has(l.id));

  return hubs.reduce<HubTypeEnum[]>((acc, hub) => {
    const isHubAssignedToLeg = activeLegs.some((leg) =>
      [leg.departureLocation.locationId, leg.arrivalLocation.locationId].includes(hub.hubId),
    );

    if (isHubAssignedToLeg) return acc;

    return [...acc, hub.hubType];
  }, []);
};

export const hasDryRunRouteLegs = (logisticsProjection?: LogisticsProjectionType): boolean => {
  return !!logisticsProjection?.routes?.some(
    (route) => route.routeType === RouteTypeEnum.DryRun && route.legs.length > 0,
  );
};
